import { useMemo, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEMO } from '../lib/demo';
import { RISK_HEX, BRAND_HEX } from '../lib/tokens';
import { useAlerts, useExpedientes, useOnboardingTrend } from '../lib/hooks';
import { Button, Card, cn, EmptyState, PageHeader, Pill, Skeleton } from '../components/ui';
import { Donut, TrendArea, type Segment } from '../components/charts';
import { EXPEDIENTES_SEED } from '../lib/phase1';
import type { NivelRiesgo } from '../lib/phase1';
import {
  FolderOpen,
  CalendarClock,
  ShieldAlert,
  Bell,
  Compass,
  Clock,
  TrendingUp,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';

/** Two glance-able buckets instead of six flat rows — substates show as tags. */
const STATE_GROUPS: { key: string; label: string; color: string; states: string[] }[] = [
  {
    key: 'proceso',
    label: 'En proceso',
    color: RISK_HEX.medio,
    states: ['Nuevo', 'En Revisión', 'Docs Pendientes', 'Escalado', 'Actualización Requerida'],
  },
  { key: 'aldia', label: 'Al día', color: BRAND_HEX.primary, states: ['Aprobado', 'Activo'] },
];
const RIESGO_COLOR: Record<NivelRiesgo, string> = { ...RISK_HEX };

function OnboardingDelta({ points }: { points: number[] }) {
  const thisWeek = points[points.length - 1] ?? 0;
  const lastWeek = points[points.length - 2] ?? 0;
  const delta = thisWeek - lastWeek;
  const label = delta >= 0 ? `+${delta} esta semana` : `${delta} esta semana`;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 font-mono text-[11px] font-semibold text-primary-dark">
      <TrendingUp className="h-3.5 w-3.5" strokeWidth={2.25} /> {label}
    </span>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: exps, isLoading, isError, refetch } = useExpedientes();
  const { data: alerts } = useAlerts();
  const { data: trend } = useOnboardingTrend();

  const m = useMemo(() => {
    const list = exps ?? [];
    const byEstado = new Map<string, number>();
    const byRiesgo = new Map<string, number>();
    let porVencer = 0;
    for (const e of list) {
      byEstado.set(e.estado, (byEstado.get(e.estado) ?? 0) + 1);
      byRiesgo.set(e.riesgo, (byRiesgo.get(e.riesgo) ?? 0) + 1);
      porVencer += e.docs.filter((d) => d.estado === 'Por Vencer').length;
    }
    return {
      total: list.length,
      altos: list.filter((e) => e.riesgo === 'alto').length,
      porVencer,
      byEstado: [...byEstado.entries()],
      byRiesgo: [...byRiesgo.entries()] as [NivelRiesgo, number][],
      expiries: list.flatMap((e) =>
        e.docs
          .filter((d) => d.estado === 'Por Vencer')
          .map((d) => ({ doc: d.name, cliente: e.name, expiry: d.expiry, id: e.id }))
      ),
    };
  }, [exps]);

  const total = m.total || 1;
  const RLAB: Record<NivelRiesgo, string> = { bajo: 'Bajo', medio: 'Medio', alto: 'Alto', sin: 'Sin evaluar' };
  const riskSegments: Segment[] = m.byRiesgo.map(([r, n]) => ({
    label: RLAB[r],
    value: n,
    color: RIESGO_COLOR[r],
  }));

  return (
    <div>
      <PageHeader
        title="Panel de Cumplimiento"
        subtitle="Resumen operativo del tenant · clientes, riesgo y vencimientos"
        actions={<Button onClick={() => navigate('/expedientes/nuevo')}>＋ Nuevo expediente</Button>}
      />

      {/* guided start — demo only: it links to the first seeded expediente (the
          OFAC-match case), which doesn't exist on the real backend. The header's
          "Nuevo expediente" covers onboarding on the live app. */}
      {DEMO && (
        <Card className="mb-5 flex flex-wrap items-center gap-4 rounded-3xl border-primary/30 bg-primary/5">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/15">
            <Compass className="h-5 w-5 text-primary-dark" strokeWidth={1.75} />
          </span>
          <div className="flex-1">
            <div className="font-serif text-lg font-medium">Recorrido guiado</div>
            <p className="text-sm text-ink-soft">
              Onboarde un cliente de punta a punta, o abra{' '}
              <b>{EXPEDIENTES_SEED[0]?.name ?? 'un expediente de ejemplo'}</b> para ver las
              validaciones y el riesgo en acción.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/revision/${EXPEDIENTES_SEED[0]?.id ?? ''}`)}>
              Ver ejemplo
            </Button>
            <Button onClick={() => navigate('/expedientes/nuevo')}>Empezar onboarding →</Button>
          </div>
        </Card>
      )}

      {/* KPIs */}
      {isError ? (
        <Card className="mb-5 rounded-3xl">
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title="No se pudo cargar el panel"
            hint="Verifica tu conexión e inténtalo de nuevo."
            action={<Button onClick={() => void refetch()}>Reintentar</Button>}
          />
        </Card>
      ) : isLoading ? (
        <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-3xl" />
          ))}
        </div>
      ) : (
        <div data-tour="panel-kpis" className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Kpi icon={FolderOpen} label="Expedientes" value={m.total} sub="activos en el tenant" />
          <Kpi
            icon={CalendarClock}
            label="Documentos por vencer"
            value={m.porVencer}
            sub="próximos 90 días"
            tone="amber"
            onClick={() => navigate('/documentos?filter=por-vencer')}
          />
          <Kpi icon={ShieldAlert} label="Riesgo alto / PEP" value={m.altos} sub="requieren EDD" tone="red" />
          <Kpi icon={Bell} label="Alertas abiertas" value={alerts?.length ?? 0} sub="bandeja unificada" />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
        <Card className="rounded-3xl p-0">
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h3 className="font-serif text-base font-medium text-ink">Clientes por estado</h3>
            <button className="font-mono text-[10.5px] text-primary-dark" onClick={() => navigate('/revision')}>
              Ver expedientes →
            </button>
          </div>
          <div className="px-5 py-3">
            {STATE_GROUPS.map((g) => (
              <StateGroup
                key={g.key}
                label={g.label}
                color={g.color}
                total={total}
                counts={g.states.map((estado) => [estado, m.byEstado.find(([e]) => e === estado)?.[1] ?? 0] as const)}
              />
            ))}
            {(() => {
              const rechazados = m.byEstado.find(([e]) => e === 'Rechazado')?.[1] ?? 0;
              return rechazados > 0 ? (
                <p className="mt-1 font-mono text-[10.5px] text-ink-faint">{rechazados} rechazado(s)</p>
              ) : null;
            })()}
          </div>
        </Card>

        <Card data-tour="panel-risk" className="rounded-3xl p-0">
          <div className="border-b border-line px-5 py-4">
            <h3 className="font-serif text-base font-medium text-ink">Distribución de riesgo</h3>
          </div>
          <div className="flex flex-col items-center gap-5 px-5 py-5 sm:flex-row sm:gap-6">
            <Donut segments={riskSegments} />
            <div className="w-full space-y-2.5 sm:flex-1">
              {riskSegments.map((s) => (
                <div key={s.label} className="flex items-center gap-2.5 text-sm">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />
                  <span className="flex-1 font-medium">{s.label}</span>
                  <span className="font-mono text-xs text-ink-soft">{s.value}</span>
                  <span className="w-10 text-right font-mono text-[11px] text-ink-faint">
                    {Math.round((s.value / total) * 100)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card data-tour="panel-chart" className="rounded-3xl lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-serif text-base font-medium text-ink">Actividad de onboarding · últimas 8 semanas</h3>
            {trend && <OnboardingDelta points={trend.points} />}
          </div>
          <TrendArea
            points={trend?.points ?? [0, 0, 0, 0, 0, 0, 0, 0]}
            {...(trend?.labels !== undefined ? { labels: trend.labels } : {})}
          />
        </Card>

        <Card className="rounded-3xl p-0">
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h3 className="flex items-center gap-2 font-serif text-base font-medium text-ink">
              <Clock className="h-4 w-4 text-ink-soft" strokeWidth={1.75} />
              Próximos vencimientos
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[460px] w-full text-sm">
              <tbody>
                {m.expiries.length === 0 && (
                  <tr>
                    <td className="px-5 py-4 text-ink-faint">Sin vencimientos próximos.</td>
                  </tr>
                )}
                {m.expiries.map((x, i) => (
                  <tr
                    key={i}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/revision/${x.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/revision/${x.id}`);
                      }
                    }}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                  >
                    <td className="px-5 py-3 font-bold">{x.doc}</td>
                    <td className="px-5 py-3 text-ink-soft">{x.cliente}</td>
                    <td className="px-5 py-3">
                      <Pill tone="amber">{x.expiry}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="rounded-3xl p-0">
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <h3 className="flex items-center gap-2 font-serif text-base font-medium text-ink">
              <Bell className="h-4 w-4 text-ink-soft" strokeWidth={1.75} />
              Bandeja de alertas
            </h3>
            <button className="font-mono text-[10.5px] text-primary-dark" onClick={() => navigate('/alertas')}>
              Ver todas →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[460px] w-full text-sm">
              <tbody>
                {(alerts ?? []).length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-5 py-4 text-ink-faint">Sin alertas activas.</td>
                  </tr>
                )}
                {(alerts ?? []).map((a) => (
                  <tr
                    key={a.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate('/alertas')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/alertas');
                      }
                    }}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
                  >
                    <td className="px-5 py-3">
                      <div className="font-bold">{a.tipo}</div>
                      <div className="font-mono text-[10px] text-ink-faint">{a.id}</div>
                    </td>
                    <td className="px-5 py-3 text-ink-soft">{a.cliente}</td>
                    <td className="px-5 py-3">
                      <Pill tone={a.prioridad === 'Alta' ? 'red' : 'amber'}>{a.estado}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  sub: string;
  tone?: 'amber' | 'red';
  onClick?: () => void;
}) {
  const color = tone === 'amber' ? 'text-tone-amber' : tone === 'red' ? 'text-tone-red' : 'text-ink';
  return (
    <Card
      className={cn(
        'rounded-3xl p-5',
        onClick && 'cursor-pointer transition-colors hover:border-ink-faint'
      )}
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            onClick,
            onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter') onClick();
              if (e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      <div className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
        {label}
      </div>
      <div className={`mt-1.5 font-serif text-4xl font-medium ${color}`}>{value}</div>
      <div className="mt-0.5 font-mono text-[10px] text-ink-faint">{sub}</div>
    </Card>
  );
}

function StateGroup({
  label,
  color,
  total,
  counts,
}: {
  label: string;
  color: string;
  total: number;
  counts: readonly (readonly [string, number])[];
}) {
  const groupTotal = counts.reduce((sum, [, n]) => sum + n, 0);
  return (
    <div className="py-3 first:pt-1">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-2 text-[12.5px] font-bold">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
          {label}
        </span>
        <span className="font-mono text-[11.5px] text-ink-soft">{groupTotal}</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-md bg-canvas">
        <div
          className="h-full rounded-md"
          style={{ width: `${(groupTotal / total) * 100}%`, background: color }}
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {counts
          .filter(([, n]) => n > 0)
          .map(([estado, n]) => (
            <span
              key={estado}
              className="rounded-md bg-canvas px-2 py-1 font-mono text-[10px] text-ink-faint"
            >
              {estado} · {n}
            </span>
          ))}
      </div>
    </div>
  );
}
